import { useEffect } from 'react';
import { Control, useController } from 'react-hook-form';

import type { TemplateFormSchema } from '../validations/edit-template.validation';

import { useSharePlan } from '../../../../hooks/share-plan/use-share-plan';
import { selectedCompanyStorage } from '../../../../storage/company/selected-company.util';
import { SharePlanVestingType } from '../../../../types/pool-plans.types';
import { TemplateCategory } from '../enums/template.const';
import { MappingEditorStore } from '../store/mapping-editor.store';
import { useTemplateFieldAvailability } from './use-template-field-availability.hook';

export const useSyncTemplateContext = (control: Control<TemplateFormSchema>) => {
  const companyId = selectedCompanyStorage(({ companyId }) => companyId);
  const { field: categoryField } = useController({ control, name: 'stepOne.category' });
  const { field: subCategoryField } = useController({ control, name: 'stepOne.subCategory' });
  const { field: equityPlanField } = useController({ control, name: 'stepOne.equityPlan' });

  const category = categoryField.value;
  const subCategory = subCategoryField.value;
  const equityPlanId = equityPlanField.value ?? '';

  const { sharePlan: fetchedPlan } = useSharePlan({
    companyId: companyId ?? '',
    planId: equityPlanId,
  });
  const selectedEquityPlan = MappingEditorStore(({ selectedEquityPlan }) => selectedEquityPlan);
  const setSelectedEquityPlan = MappingEditorStore(
    ({ setSelectedEquityPlan }) => setSelectedEquityPlan,
  );
  const setTemplateContext = MappingEditorStore(({ setTemplateContext }) => setTemplateContext);
  const { availability } = useTemplateFieldAvailability(
    equityPlanId,
    category === TemplateCategory.GRANT,
  );

  useEffect(() => {
    if (category === TemplateCategory.GRANT && equityPlanId && fetchedPlan) {
      setSelectedEquityPlan(fetchedPlan);
    } else if (!equityPlanId || category !== TemplateCategory.GRANT) {
      setSelectedEquityPlan(null);
    }
  }, [category, equityPlanId, fetchedPlan, setSelectedEquityPlan]);

  const milestonesCount =
    selectedEquityPlan?.vesting?.type === SharePlanVestingType.TARGET_BASED
      ? selectedEquityPlan.vesting.milestones.length
      : 0;

  const periodsCount =
    selectedEquityPlan?.vesting?.type === SharePlanVestingType.TIME_BASED_DYNAMIC
      ? selectedEquityPlan.vesting.periods.length
      : 0;

  useEffect(() => {
    if (!category || !subCategory) return;

    if (category === TemplateCategory.GRANT) {
      setTemplateContext({
        category: TemplateCategory.GRANT,
        subCategory,
        planType: selectedEquityPlan?.type,
        vestingType: selectedEquityPlan?.vesting?.type,
        hasAccelerationDate: availability?.hasAccelerationDate,
        hasCliff: availability?.hasCliff,
        hasVesting: availability?.hasVesting ?? Boolean(selectedEquityPlan?.vesting),
        milestonesCount,
        periodsCount,
      });
    } else {
      setTemplateContext({ category, subCategory });
    }
  }, [
    category,
    subCategory,
    selectedEquityPlan,
    availability?.hasAccelerationDate,
    availability?.hasCliff,
    availability?.hasVesting,
    milestonesCount,
    periodsCount,
    setTemplateContext,
  ]);
};
